import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import type {
  PackageLabelAssignmentWithName,
  PackageLabelScope,
} from '../../../../shared/model-typings/packagelabelassignment';
import { useOrganizationPackageLabels, usePackageLabelAssignments } from '../../../hooks/api/packageLabels';
import type { Permission } from '../../../shared/permissions';
import { Action, ResourceType, useHasPermission } from '../../../shared/permissions';
import { BUTTON_TYPE, FormGroup, Icon, LabeledMultiTextInput, LabeledSelect, Select, icons } from '../../generic';
import InfoPanel from '../../generic/InfoPanel';
import Loader from '../../generic/Loader';
import { Tab } from './types';

import Locator from '../../../../shared/Locator';
import Button, { BUTTON_SIZE } from '../../generic/Button';
import styles from './ModalManagePackageLabels.module.less';

const TabScopes: Record<Tab, PackageLabelScope> = {
  [Tab.Revision]: 'revision',
  [Tab.Project]: 'project',
  [Tab.Global]: 'org',
};

const AllLabelsSelected: VoidFunctionComponent<{ inputValue: string }> = () => (
  <span>All available labels have been selected. Create new labels in your organization settings.</span>
);

type Props = {
  tab: Tab;
  activeTab: Tab;
  packageId: string;
  helpText: string;
  parentRevisionLocator?: string;
};

const TabContent: VoidFunctionComponent<Props> = ({ tab, activeTab, packageId, helpText, parentRevisionLocator }) => {
  const { data: labels, isLoading: areLabelsLoading } = useOrganizationPackageLabels();
  const { data: labelAssignments, isLoading: areAssignmentsLoading } = usePackageLabelAssignments(packageId);
  const hasPermission = useHasPermission();

  /**
   * The user must have global permissions to edit global scoped labels.
   * If the user has global OR local permissions, then they can edit project and revision scoped labels.
   */
  const permissions: Permission[] = [{ action: Action.Set, resourceType: ResourceType.PackageLabel }];
  if (tab !== Tab.Global) {
    permissions.push({ action: Action.SetLocal, resourceType: ResourceType.PackageLabel, projectId: packageId });
  }
  const hasNoPermission = !permissions.some((permission) => hasPermission(permission));

  if (tab !== activeTab) {
    return null;
  }

  if (areLabelsLoading || areAssignmentsLoading) {
    return <Loader />;
  }

  const labelMap = _.keyBy(labels?.packageLabels, (label) => label.id);
  const labelOptions = labels?.packageLabels.map((label) => ({ value: label.id, label: label.name }));

  const isScopeMatch = (assignment: PackageLabelAssignmentWithName) => {
    return assignment.scope === TabScopes[tab];
  };

  const isParentProjectMatch = (assignment: PackageLabelAssignmentWithName) => {
    if (TabScopes[tab] !== 'project') {
      return true;
    }
    return assignment.scopeId === null || assignment.scopeId === Locator.parsePackageString(parentRevisionLocator!);
  };

  const isParentRevisionMatch = (assignment: PackageLabelAssignmentWithName) => {
    if (TabScopes[tab] !== 'revision') {
      return true;
    }
    return assignment.scopeId === null || assignment.scopeId === parentRevisionLocator;
  };

  const assignments = labelAssignments?.packageLabelAssignments.filter(
    (assignment) => isScopeMatch(assignment) && isParentProjectMatch(assignment) && isParentRevisionMatch(assignment),
  );
  const [allVersionAssignments, specificVersionAssignments] = _.partition(
    assignments,
    (assignment) => assignment.packageVersion == null,
  );
  const defaultAllVersionAssignments = allVersionAssignments.map((assignment) => ({
    value: assignment.labelId,
    label: labelMap[assignment.labelId]?.name,
  }));

  const defaultSpecificVersionAssignments = _.mapValues(
    _.groupBy(specificVersionAssignments, (assignment) => assignment.packageVersion),
    (assignmentGroup) =>
      assignmentGroup.map((assignment) => ({ value: assignment.labelId, label: labelMap[assignment.labelId]?.name })),
  );

  return (
    <>
      <p className={styles.helpText}>{helpText}</p>
      {hasNoPermission && <InfoPanel>You do not have permission to make changes here.</InfoPanel>}
      <FormGroup>
        <LabeledSelect
          id="all-versions"
          label="All versions"
          isMulti
          onChange={() => null}
          options={labelOptions}
          closeMenuOnSelect={false}
          noOptionsMessage={AllLabelsSelected}
          placeholder="Assign labels..."
          defaultValue={defaultAllVersionAssignments}
        />
        {Object.entries(defaultSpecificVersionAssignments).map(([version, versionAssignments]) => (
          <div key={version}>
            <LabeledMultiTextInput
              id="specific-versions"
              label="Specific Versions"
              values={[version]}
              maxItems={10}
              shouldAllowDuplicates={false}
              isCaseInsensitive
              placeholder="Package versions to assign labels to"
            />
            <Select
              id="specific-version-labels"
              isMulti
              onChange={() => null}
              options={labelOptions}
              defaultValue={versionAssignments}
              closeMenuOnSelect={false}
              noOptionsMessage={AllLabelsSelected}
              placeholder="Assign labels..."
            />
            <div className={styles.removeButton}>
              <Button title="Remove" onClick={() => null} type={BUTTON_TYPE.TEXT} size={BUTTON_SIZE.MEDIUM}>
                <span className={styles.removeText}>
                  <Icon icon={icons.xCircle} />
                  Remove
                </span>
              </Button>
            </div>
          </div>
        ))}
        <Button
          icon={icons.plusCircleO}
          title="Remove"
          onClick={() => null}
          type={BUTTON_TYPE.TEXT}
          size={BUTTON_SIZE.MEDIUM}
        >
          Add Version
        </Button>
      </FormGroup>
    </>
  );
};

export default TabContent;
